import {
  formatHours,
  formatReportValue,
  type ReportDocument,
  type ReportRow,
} from '@codepass/shared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui';

/**
 * The diagrams on /rapporter/pipeline: the waiting hours split into internal and external, when
 * they fall due, and the year's registered time month by month. Drawn from the report's own
 * sections - `split`, `deadlines` and `months` - so the bars and the tables below cannot say
 * different things. The CSV and the PDF keep the tables.
 */

const EXTERNAL = 'bg-brand';
const INTERNAL = 'bg-amber-400';

const num = (value: ReportRow[string] | undefined): number | null =>
  typeof value === 'number' ? value : null;

const hours = (value: number): string => `${formatHours(value)} timer`;

function Legend() {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-muted">
      <li className="flex items-center gap-1.5">
        <span aria-hidden className={`h-3 w-3 rounded-sm ${EXTERNAL}`} />
        Eksternt
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden className={`h-3 w-3 rounded-sm ${INTERNAL}`} />
        Internt
      </li>
    </ul>
  );
}

/**
 * Horizontal bars, external and internal stacked, on one scale. A row whose values are null - a
 * month that has not begun - is an empty bar and a dash, not 0.
 */
function StackedBars({
  items,
}: {
  items: Array<{ label: string; external: number | null; internal: number | null; aside?: string }>;
}) {
  const max = Math.max(...items.map((item) => (item.external ?? 0) + (item.internal ?? 0)), 1);

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const known = item.external !== null && item.internal !== null;
        const total = (item.external ?? 0) + (item.internal ?? 0);
        return (
          <li key={item.label} className="flex items-center gap-3">
            <span className="w-28 shrink-0 truncate text-xs text-ink-muted" title={item.label}>
              {item.label}
            </span>
            <span
              className="flex h-5 flex-1 overflow-hidden rounded bg-canvas"
              title={
                known
                  ? `Eksternt ${hours(item.external ?? 0)} · internt ${hours(item.internal ?? 0)}`
                  : undefined
              }
            >
              <span
                className={EXTERNAL}
                style={{ width: `${((item.external ?? 0) / max) * 100}%` }}
              />
              <span
                className={INTERNAL}
                style={{ width: `${((item.internal ?? 0) / max) * 100}%` }}
              />
            </span>
            <span className="w-28 shrink-0 text-right text-xs tabular">
              {known ? hours(total) : '—'}
              {item.aside ? (
                <span className="block text-[11px] text-ink-muted">{item.aside}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function PipelineCharts({ report }: { report: ReportDocument }) {
  const section = (id: string) => report.sections.find((entry) => entry.id === id);
  const split = section('split');
  const deadlines = section('deadlines');
  const months = section('months');

  const share = (kind: string) => split?.rows.find((row) => row.kind === kind);
  const external = share('Eksternt');
  const internal = share('Internt');
  const externalHours = num(external?.hours) ?? 0;
  const internalHours = num(internal?.hours) ?? 0;
  const waiting = externalHours + internalHours;

  return (
    <>
      <Card className="mb-5">
        <CardHeader>
          <CardTitle>Ventende timer nu</CardTitle>
          <p className="mt-1 text-xs text-ink-muted">
            Estimat minus brugt tid på de åbne opgaver i OnePro.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          {waiting > 0 ? (
            <>
              <div
                className="flex h-8 overflow-hidden rounded"
                role="img"
                aria-label={`Eksternt ${formatReportValue('percent', num(external?.share) ?? null)}, internt ${formatReportValue('percent', num(internal?.share) ?? null)}`}
              >
                <div
                  className={EXTERNAL}
                  style={{ width: `${(externalHours / waiting) * 100}%` }}
                />
                <div
                  className={INTERNAL}
                  style={{ width: `${(internalHours / waiting) * 100}%` }}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { label: 'Eksternt', row: external, color: EXTERNAL, value: externalHours },
                  { label: 'Internt', row: internal, color: INTERNAL, value: internalHours },
                ].map((entry) => (
                  <div key={entry.label} className="flex items-baseline gap-2">
                    <span aria-hidden className={`h-3 w-3 shrink-0 rounded-sm ${entry.color}`} />
                    <span className="text-sm font-medium">{entry.label}</span>
                    <span className="text-sm tabular">
                      {formatReportValue('percent', num(entry.row?.share) ?? null)}
                    </span>
                    <span className="text-xs text-ink-muted">
                      {hours(entry.value)} · {num(entry.row?.tasks) ?? 0} åbne opgaver
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs text-ink-muted">Ingen ventende timer.</p>
          )}
        </CardBody>
      </Card>

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ventende timer pr. deadline</CardTitle>
            <div className="mt-2">
              <Legend />
            </div>
          </CardHeader>
          <CardBody>
            {deadlines && deadlines.rows.length > 0 ? (
              <StackedBars
                items={deadlines.rows.map((row) => ({
                  label: String(row.period ?? ''),
                  external: num(row.external),
                  internal: num(row.internal),
                }))}
              />
            ) : (
              <p className="text-xs text-ink-muted">Ingen ventende timer.</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Registreret tid pr. måned, {report.year}</CardTitle>
            <div className="mt-2">
              <Legend />
            </div>
          </CardHeader>
          <CardBody>
            {months && months.rows.length > 0 ? (
              <StackedBars
                items={months.rows.map((row) => ({
                  label: String(row.month ?? ''),
                  external: num(row.external),
                  internal: num(row.internal),
                  aside:
                    num(row.internal_pct) === null
                      ? undefined
                      : `internt ${formatReportValue('percent', num(row.internal_pct))}`,
                }))}
              />
            ) : (
              <p className="text-xs text-ink-muted">Ingen registreret tid.</p>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
